import * as React from "react";
import { Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/lib/auth/hooks";
import { useI18n } from "@/app/lib/i18n";
import { useFeatureFlagsBootstrap } from "@/app/lib/featureFlags/hooks";
import { DarkModeToggle } from "@/components/DarkModeToggle";
import { LanguageToggle } from "@/components/LanguageToggle";
import { UserMenu } from "@/ui/components/UserMenu";
import type { NavUser } from "@/ui/components/UserMenu";
import * as s from "./CertificationShell.style.css";

export const HEADER_HEIGHT = 64;

const CHROMELESS_PATHS = new Set(["/redirecting"]);

export function CertificationShell() {
  const { isAuthenticated, user, logout } = useAuth();
  const { openLanguagePicker, t, tx, currentLanguage } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  useFeatureFlagsBootstrap();

  const handleLogout = async () => {
    await logout();
    navigate({ to: "/", replace: true });
  };

  if (CHROMELESS_PATHS.has(location.pathname)) {
    return <Outlet />;
  }

  const navUser: NavUser | null = user
    ? {
        name: `${user.firstName} ${user.lastName}`.trim() || user.email,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        avatarUrl: user.avatarUrl,
        emailVerified: user.emailVerified,
        initials:
          (`${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase() ||
            user.email[0]?.toUpperCase()) ??
          "?",
        onChangeLanguageClick: openLanguagePicker,
        onLogoutClick: handleLogout,
        changeLanguageLabel: t("shared.userMenu.changeLanguage"),
        logOutLabel: t("shared.userMenu.logOut"),
        languageName: currentLanguage?.name,
        languageFlagUrl: currentLanguage?.flagUrl,
      }
    : null;

  return (
    <div className={s.page}>
      <header className={s.header}>
        <Link to="/" className={s.brand}>
          <img src="/logos/lion_square.svg" alt="" width={28} height={28} className={s.brandLogo} />
          <span className={s.brandText}>{tx("certification.shell.brand")}</span>
        </Link>

        {isAuthenticated && navUser && (
          <div className={s.headerActions}>
            <LanguageToggle placement="header" />
            <DarkModeToggle placement="header" />
            <UserMenu user={navUser} compactOnMobile />
          </div>
        )}
      </header>

      <Outlet />
    </div>
  );
}
